import React from 'react';
import { Play } from 'lucide-react';
import { MELODIES } from '../constants/melodies';

export default function AlarmSettings({
  alarmHour,
  alarmMinute,
  snoozeMinutes,
  selectedMelody,
  growing,
  volume,
  alarmActive,
  alarmRinging,
  setAlarmHour,
  setAlarmMinute,
  setSnoozeMinutes,
  setSelectedMelody,
  setGrowing,
  setVolume,
  setAlarmActive,
  testSound,
  stopAlarm,
  snoozeAlarm
}) {
  const goFullscreen = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  };

  return (
    <div className="bg-cyan-600/40 backdrop-blur-md rounded-3xl p-8 mb-8 border border-cyan-400/30 shadow-2xl">
      {/* Time and Snooze Settings */}
      <div className="grid grid-cols-2 gap-8 mb-6">
        <TimeSelector
          label="When alarm?"
          hour={alarmHour}
          minute={alarmMinute}
          onHourChange={setAlarmHour}
          onMinuteChange={setAlarmMinute}
        />
        
        <SnoozeSelector
          value={snoozeMinutes}
          onChange={setSnoozeMinutes}
        />
      </div>

      {/* Melody Selection */}
      <MelodySelector
        selectedMelody={selectedMelody}
        growing={growing}
        onMelodyChange={setSelectedMelody}
        onGrowingChange={setGrowing}
        onTestSound={testSound}
      />

      {/* Volume Control */}
      <VolumeControl
        volume={volume}
        onVolumeChange={setVolume}
      />

      {/* Action Buttons */}
      <ActionButtons
        alarmRinging={alarmRinging}
        alarmActive={alarmActive}
        onSetAlarm={() => setAlarmActive(!alarmActive)}
        onStopAlarm={stopAlarm}
        onSnoozeAlarm={snoozeAlarm}
        onFullscreen={goFullscreen}
      />
    </div>
  );
}

function TimeSelector({ label, hour, minute, onHourChange, onMinuteChange }) {
  return (
    <div>
      <label className="block text-white font-semibold mb-3 text-lg">
        {label}
      </label>
      <div className="flex gap-3">
        <select
          value={hour}
          onChange={(e) => onHourChange(e.target.value)}
          className="flex-1 px-4 py-3 bg-white/90 rounded-xl text-gray-700 font-semibold text-xl focus:outline-none focus:ring-2 focus:ring-cyan-400"
        >
          {Array.from({ length: 24 }, (_, i) => (
            <option key={i} value={i.toString().padStart(2, '0')}>
              {i.toString().padStart(2, '0')}
            </option>
          ))}
        </select>
        <select
          value={minute}
          onChange={(e) => onMinuteChange(e.target.value)}
          className="flex-1 px-4 py-3 bg-white/90 rounded-xl text-gray-700 font-semibold text-xl focus:outline-none focus:ring-2 focus:ring-cyan-400"
        >
          {Array.from({ length: 60 }, (_, i) => (
            <option key={i} value={i.toString().padStart(2, '0')}>
              {i.toString().padStart(2, '0')}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SnoozeSelector({ value, onChange }) {
  return (
    <div>
      <label className="block text-white font-semibold mb-3 text-lg">
        Snooze (minutes)
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-white/90 rounded-xl text-gray-700 font-semibold text-xl focus:outline-none focus:ring-2 focus:ring-cyan-400"
      >
        {[5, 10, 15, 20, 30].map(min => (
          <option key={min} value={min.toString().padStart(2, '0')}>
            {min.toString().padStart(2, '0')}
          </option>
        ))}
      </select>
    </div>
  );
}

function MelodySelector({ selectedMelody, growing, onMelodyChange, onGrowingChange, onTestSound }) {
  return (
    <div className="mb-6">
      <label className="block text-white font-semibold mb-3 text-lg">
        Choose melody
      </label>
      <div className="flex gap-3 items-center">
        <select
          value={selectedMelody}
          onChange={(e) => onMelodyChange(e.target.value)}
          className="flex-1 px-4 py-3 bg-cyan-700/60 text-white rounded-xl font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-cyan-400"
        >
          {MELODIES.map(melody => (
            <option key={melody.id} value={melody.id}>{melody.name}</option>
          ))}
        </select>
        <button
          onClick={onTestSound}
          className="p-3 bg-cyan-500 hover:bg-cyan-600 rounded-xl transition-colors"
        >
          <Play className="w-6 h-6 text-white" />
        </button>
        <div className="flex items-center gap-3 bg-cyan-700/40 px-4 py-3 rounded-xl">
          <span className="text-white font-semibold">Growing?</span>
          <ToggleSwitch checked={growing} onChange={onGrowingChange} />
        </div>
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <label className="relative inline-block w-12 h-6">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-full h-full bg-gray-400 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition-colors">
        <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform ${checked ? 'translate-x-6' : ''}`}></div>
      </div>
    </label>
  );
}

function VolumeControl({ volume, onVolumeChange }) {
  return (
    <div className="mb-6">
      <label className="block text-white font-semibold mb-3 text-lg">
        Volume: {volume}%
      </label>
      <input
        type="range"
        min="0"
        max="100"
        value={volume}
        onChange={(e) => onVolumeChange(Number(e.target.value))}
        className="w-full h-3 bg-white/30 rounded-lg appearance-none cursor-pointer slider"
        style={{
          background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${volume}%, rgba(255,255,255,0.3) ${volume}%, rgba(255,255,255,0.3) 100%)`
        }}
      />
    </div>
  );
}

function ActionButtons({ alarmRinging, alarmActive, onSetAlarm, onStopAlarm, onSnoozeAlarm, onFullscreen }) {
  if (alarmRinging) {
    return (
      <div className="flex gap-4">
        <button
          onClick={onStopAlarm}
          className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-lg transition-colors shadow-lg"
        >
          Stop
        </button>
        <button
          onClick={onSnoozeAlarm}
          className="flex-1 py-4 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold text-lg transition-colors shadow-lg"
        >
          Snooze
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      <button
        onClick={onSetAlarm}
        className={`flex-1 py-4 rounded-xl font-bold text-lg transition-colors shadow-lg ${
          alarmActive
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-cyan-500 hover:bg-cyan-600 text-white'
        }`}
      >
        {alarmActive ? '✓ Alarm Set' : 'Set Alarm'}
      </button>
    </div>
  );
}